/* eslint-disable @typescript-eslint/naming-convention */
import { GateType } from 'shared-utils/views';
import { ParameterInfos } from 'ut-business/parameter';

import {
  DEFAULT_INSPECTION_MODE,
  GATE_WIDTH_METRIC,
  GATE_WIDTH_TIME_OF_FLIGHT,
  POSITION_IMPERIAL,
  POSITION_METRIC,
  POSITION_TIME_OF_FLIGHT,
} from './parameter-infos.constants';

declare const brandGateStart: unique symbol;
type GateStart = ParameterInfos & { [brandGateStart]: never };

declare const brandGateWidth: unique symbol;
type GateWidth = ParameterInfos & { [brandGateWidth]: never };

declare const brandGateThreshold: unique symbol;
type GateThreshold = ParameterInfos & { [brandGateThreshold]: never };

// Create branded versions of the ParameterInfos type to prevent human errors below
const gateStart: GateStart = {
  metric: POSITION_METRIC,
  imperial: POSITION_IMPERIAL,
  timeOfFlight: POSITION_TIME_OF_FLIGHT,
  sourceUnit: 'm',
  inspectionMode: DEFAULT_INSPECTION_MODE,
} satisfies ParameterInfos as GateStart;

const gateWidth: GateWidth = {
  metric: GATE_WIDTH_METRIC,
  imperial: POSITION_IMPERIAL,
  timeOfFlight: GATE_WIDTH_TIME_OF_FLIGHT,
  sourceUnit: 'm',
  inspectionMode: DEFAULT_INSPECTION_MODE,
} satisfies ParameterInfos as GateWidth;

const gateThreshold: GateThreshold = {
  percent: {
    unit: '%',
    decimals: 1,
    defaultStep: 1,
    steps: [
      0.1,
      1,
      10,
    ],
    limit: {
      min: 0,
      max: 100,
    },
  },
  decibel: {
    unit: 'dB',
    decimals: 2,
    defaultStep: 1,
    steps: [
      0.01,
      0.1,
      1,
      10,
    ],
    limit: {
      min: 0,
      max: 100,
    },
  },
  inspectionMode: DEFAULT_INSPECTION_MODE,
  sourceUnit: '%',
} satisfies ParameterInfos as GateThreshold;

const baseParameterInfo: ParameterInfos = {
  inspectionMode: DEFAULT_INSPECTION_MODE,
} satisfies ParameterInfos;

const enableParameterInfos: ParameterInfos = {
  inspectionMode: {
    setup: {
      isReadOnly: false,
    },
    inspection: {
      isReadOnly: true,
    },
    pause: {
      isReadOnly: true,
    },
    analysis: {
      isReadOnly: true,
    },
  },
} satisfies ParameterInfos;

// Currently, these are the only gates with configurations in Windview/Aeroview
type WindviewGateTypes = Extract<`${GateType}`, 'A' | 'B' | 'C' | 'D' | 'I'>;

// Enforce every gate for a given parameter has the same configuration
// eg: all gate start parameters share the same config
type GateParameters =
  Record<`Gate_${WindviewGateTypes}_${'Synchro' | 'Peak' | 'Measure' | 'Enable' | 'ThresholdPolarity'}`, ParameterInfos>
  & Record<`Gate_${WindviewGateTypes}_Start`, GateStart>
  & Record<`Gate_${WindviewGateTypes}_Width`, GateWidth>
  & Record<`Gate_${WindviewGateTypes}_Threshold`, GateThreshold>;

export const gatesParameters: GateParameters = {

  Gate_A_Start: gateStart,
  Gate_B_Start: gateStart,
  Gate_C_Start: gateStart,
  Gate_D_Start: gateStart,
  Gate_I_Start: gateStart,
  Gate_A_Width: gateWidth,
  Gate_B_Width: gateWidth,
  Gate_C_Width: gateWidth,
  Gate_D_Width: gateWidth,
  Gate_I_Width: gateWidth,
  Gate_A_Threshold: gateThreshold,
  Gate_B_Threshold: gateThreshold,
  Gate_C_Threshold: gateThreshold,
  Gate_D_Threshold: gateThreshold,
  Gate_I_Threshold: gateThreshold,
  Gate_A_Synchro: baseParameterInfo,
  Gate_B_Synchro: baseParameterInfo,
  Gate_C_Synchro: baseParameterInfo,
  Gate_D_Synchro: baseParameterInfo,
  Gate_I_Synchro: baseParameterInfo,
  Gate_A_ThresholdPolarity: baseParameterInfo,
  Gate_B_ThresholdPolarity: baseParameterInfo,
  Gate_C_ThresholdPolarity: baseParameterInfo,
  Gate_D_ThresholdPolarity: baseParameterInfo,
  Gate_I_ThresholdPolarity: baseParameterInfo,
  Gate_A_Peak: baseParameterInfo,
  Gate_B_Peak: baseParameterInfo,
  Gate_C_Peak: baseParameterInfo,
  Gate_D_Peak: baseParameterInfo,
  Gate_I_Peak: baseParameterInfo,
  Gate_A_Measure: baseParameterInfo,
  Gate_B_Measure: baseParameterInfo,
  Gate_C_Measure: baseParameterInfo,
  Gate_D_Measure: baseParameterInfo,
  Gate_I_Measure: baseParameterInfo,
  Gate_A_Enable: enableParameterInfos,
  Gate_B_Enable: enableParameterInfos,
  Gate_C_Enable: enableParameterInfos,
  Gate_D_Enable: enableParameterInfos,
  Gate_I_Enable: enableParameterInfos,
};
